// Light/dark theme toggle. The theme is a pure CSS palette skin selected
// by ONE class on <body>:
//   LIGHT (trust) → body.theme-trust
//   DARK  (base)  → no body class (the original phosphor-green design)
//
// Persistence is server-side: on toggle we POST the new theme to
// /<locale>/theme. A separate controller sets the `gm_theme` cookie (and
// the user's pref when logged in), so every SUBSEQUENT load renders the
// correct class server-side. We only handle the INSTANT in-page flip on
// click — no localStorage, no cookie writing here. The POST is the source
// of truth.
//
// Markup contract (template-owned, in templates/base/html.tx):
//   <button class="theme-toggle" data-theme-toggle
//           data-locale="<: $locale :>"
//           data-label-light="<: l('Switch to light theme') :>"
//           data-label-dark="<: l('Switch to dark theme')  :>"
//           aria-pressed="…" aria-label="…">…icons…</button>
// aria-pressed reflects "light theme active" (the non-default state).

const TRUST = "theme-trust";

function isLight(): boolean {
  return document.body.classList.contains(TRUST);
}

// Reflect current state on the button: aria-pressed = light active, and
// the aria-label invites the OPPOSITE action (what a click will do).
function sync(btn: HTMLButtonElement): void {
  const light = isLight();
  btn.setAttribute("aria-pressed", light ? "true" : "false");
  // Clicking flips to the other theme, so the label describes the target.
  const next = light
    ? btn.dataset.labelDark
    : btn.dataset.labelLight;
  if (next) btn.setAttribute("aria-label", next);
}

function persist(locale: string, theme: "light" | "dark"): void {
  // Fire-and-forget. Fail-soft: if the POST fails the visual flip stays,
  // and the next full load may revert to the stored preference — accepted.
  fetch(`/${locale}/theme`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Requested-With": "fetch",
    },
    body: `theme=${theme}`,
  }).catch(() => {
    /* offline / 4xx / 5xx — leave the in-page flip in place */
  });
}

function setupToggle(btn: HTMLButtonElement): void {
  const locale = btn.dataset.locale || "en";
  sync(btn);

  btn.addEventListener("click", () => {
    // (a) instant in-page flip for immediate feedback
    const nowLight = document.body.classList.toggle(TRUST);
    // (b) update icon/aria from the new state
    sync(btn);
    // (c) persist server-side as the source of truth for next loads
    persist(locale, nowLight ? "light" : "dark");
  });
}

export function initThemeToggle(): void {
  document
    .querySelectorAll<HTMLButtonElement>("button[data-theme-toggle]")
    .forEach(setupToggle);
}
